/*
  Web implementation of List: renders a semantic <ul> with direct <li> children
  so screen readers get valid list structure (no divs between ul and li).
  Uses ScrollView for scrolling instead of FlatList (no virtualization on web).
  Bullets are hidden for sighted users (list-style: none). Item position in the
  list is communicated to TalkBack etc. via aria-posinset and aria-setsize on each
  <li> so "item 1 of 5" is still announced.
*/

import type { ListItem, ListProps } from "./List.types";

interface WebProps {
  useCustomListItem?: boolean;
  containerProps?: React.ComponentProps<typeof View>;
  listItemProps?: React.ComponentProps<typeof View>;
}

import React, { useId } from "react";
import { ScrollView, View, type AccessibilityRole, StyleSheet, type ViewStyle } from "react-native";

export function List({
  data,
  testID,
  style,
  a11yLabels,
  nativeID,
  ListHeaderComponent,
  ListFooterComponent,
  containerProps,
  listItemProps,
  useCustomListItem = false,
  scrollEnabled,
  ...rest
}: ListProps & WebProps) {
  const defaultTestId = `list-test-${useId()}`;
  const listTestId = testID ?? defaultTestId;
  const { description } = a11yLabels ?? {};
  const totalItems = data.length;

  return (
    <ScrollView
      style={[styles.scrollView, style]}
      contentContainerStyle={styles.contentContainer}
      testID={listTestId}
      scrollEnabled={scrollEnabled}
      {...rest}
      pointerEvents={scrollEnabled === false ? "box-none" : rest.pointerEvents}
    >
      {ListHeaderComponent != null ? (
        typeof ListHeaderComponent === "function" ? (
          <ListHeaderComponent />
        ) : (
          ListHeaderComponent
        )
      ) : null}
      <View
        nativeID={nativeID}
        testID={`container-${listTestId}`}
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        accessibilityRole={"list" as unknown as AccessibilityRole}
        accessibilityLabel={description}
        style={[styles.list, rest.contentContainerStyle]}
        {...containerProps}
      >
        {data.map((item: ListItem, index: number) =>
          useCustomListItem ? (
            <React.Fragment key={item.key}>{item.content}</React.Fragment>
          ) : (
            <View
              key={item.key}
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              accessibilityRole={"listitem" as unknown as AccessibilityRole}
              {...{
                "aria-posinset": index + 1,
                "aria-setsize": totalItems,
              }}
              {...listItemProps}
            >
              {item.content}
            </View>
          ),
        )}
      </View>
      {ListFooterComponent != null ? (
        typeof ListFooterComponent === "function" ? (
          <ListFooterComponent />
        ) : (
          ListFooterComponent
        )
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  list: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  } as ViewStyle,
});
